import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useSession } from '../../context/session-context';
import supabase from '../../supabase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs } from '../../components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import { CountrySelect } from '../../components/ui/country-select';

const LOGIN_TABS = [
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
];

const SignInPage = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [loginMethod, setLoginMethod] = useState('email');
  const [phone, setPhone] = useState<string | undefined>();
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('Logging in...');

    const credentials =
      loginMethod === 'email'
        ? { email: formValues.email, password: formValues.password }
        : { phone: phone!, password: formValues.password };

    const { data: signInData, error } =
      await supabase.auth.signInWithPassword(credentials);

    if (error) {
      setStatus(`Error: ${error.message}`);
      return;
    }

    if (signInData?.user) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, status')
        .eq('auth_id', signInData.user.id)
        .single();

      if (userError) {
        setStatus(`Error: ${userError.message}`);
        await supabase.auth.signOut();
        return;
      }

      if (userData.status !== 'active') {
        setStatus(
          'Error: Your account is inactive. Please contact your administrator.',
        );
        await supabase.auth.signOut();
        return;
      }
    }

    setStatus('');
  };

  if (session) return <Navigate to="/courses" />;

  return (
    <div className="flex flex-col min-h-screen bg-bg-main">
      <div className="flex items-center p-4">
        <button
          onClick={() => navigate('/')}
          className="hover:text-accent-purple transition-colors absolute left-4"
        >
          <ArrowLeft className="h-5 w-5 text-title" />
        </button>
        <h1 className="text-lg font-medium text-center flex-1 text-title">
          Sign In
        </h1>
      </div>

      <div className="flex justify-center my-6">
        <div className="w-16 h-16">
          <img
            src="/images/llaami.svg"
            alt="Llaami Logo"
            className="w-full h-full"
          />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
          <Tabs
            activeTab={loginMethod}
            onTabChange={setLoginMethod}
            tabs={LOGIN_TABS}
          />

          <div className="space-y-4">
            {loginMethod === 'email' ? (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-title font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formValues.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  required
                  className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                  style={{ fontSize: '16px' }}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-title font-medium">
                  Phone
                </Label>
                <PhoneInput
                  id="phone"
                  defaultCountry="ZA"
                  international
                  countryCallingCodeEditable={false}
                  value={phone}
                  onChange={setPhone}
                  placeholder="79 123 4567"
                  inputComponent={Input}
                  countrySelectComponent={CountrySelect}
                  numberInputProps={{
                    className:
                      'h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none',
                    style: { fontSize: '16px' },
                  }}
                  className="flex gap-2 items-center"
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-title font-medium">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-accent-purple hover:text-accent-purple/80"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                value={formValues.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
              disabled={status && !status.includes('Error') ? true : false}
            >
              {status && !status.includes('Error') ? status : 'Sign In'}
            </Button>

            {status && status.includes('Error') && (
              <div className="p-3 text-sm text-center text-destructive">
                {status.replace('Error: ', '')}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignInPage;
